import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useQuery } from "@apollo/client";

import { useAuthContext } from "./AuthContext";
import { setGlobalDimensions } from "../telemetry/appInsights";
import {
  buildCurrentUserQuery,
  type CurrentUserResult,
} from "../members/graphql/currentUser";
import {
  PERSONAS,
  ROLES,
  isHiddenPersona,
  normalizePersona,
  type PersonaValue,
} from "../config/roles";
import { featureFlags } from "../config/featureFlags";

// ---------------------------------------------------------------------------
// DEV-ONLY persona override (offline testing — see CurrentUserProvider)
// ---------------------------------------------------------------------------
/**
 * localStorage key for the dev persona override. Selecting a persona in the
 * PersonaBadge switcher writes here (in dev); a `?persona=CODE` URL param wins
 * on first load and is persisted into this key.
 */
const DEV_PERSONA_STORAGE_KEY = "m360.persona";

/** All 6 personas — what a dev-overridden session can switch between offline. */
const ALL_PERSONAS = Object.values(PERSONAS) as PersonaValue[];

function isKnownPersona(value: string | null | undefined): value is PersonaValue {
  return !!value && (ALL_PERSONAS as string[]).includes(value);
}

/**
 * Read the dev persona override on first render. Tri-state:
 *   - a PersonaValue → an active dev override (seed that persona offline);
 *   - `null`         → "no persona" (allow-all) was explicitly chosen in dev;
 *   - `undefined`    → no dev intent at all → let the backend query decide.
 *
 * `?persona=CODE` wins over localStorage and is persisted. The explicit
 * "no persona" clear is in-session state only (not persisted as a value — an
 * absent key reads back as `undefined`, i.e. defer to the backend next load).
 * Returns `undefined` outside dev so production never short-circuits the query.
 */
function readDevPersonaOverride(): PersonaValue | null | undefined {
  if (!import.meta.env.DEV || typeof window === "undefined") return undefined;
  try {
    const param = new URLSearchParams(window.location.search).get("persona");
    if (param) {
      if (param === "__all__") {
        // Explicit allow-all via URL — clear any stored persona, return null.
        window.localStorage.removeItem(DEV_PERSONA_STORAGE_KEY);
        return null;
      }
      const upper = param.toUpperCase();
      if (isKnownPersona(upper)) {
        window.localStorage.setItem(DEV_PERSONA_STORAGE_KEY, upper);
        return upper;
      }
    }
    const stored = window.localStorage.getItem(DEV_PERSONA_STORAGE_KEY);
    if (isKnownPersona(stored)) return stored;
  } catch {
    /* localStorage / URL unavailable — no override */
  }
  return undefined;
}

// ---------------------------------------------------------------------------
// Context shape
// ---------------------------------------------------------------------------

/**
 * The CurrentUser context exposed to all consumers via useCurrentUser().
 *
 * Persona is the PRIMARY access gate. Permission values are SECONDARY
 * refinements within a persona's already-granted surface.
 *
 * NOTE: `loading` is bounded — the `connectCurrentUser` query carries a hard
 * request timeout (see client.ts), so a hung backend aborts into `error`
 * instead of an infinite spinner. Consumers of `loading: true` must render a
 * bounded fallback (spinner with a timeout or the app's existing LoadingSpinner).
 */
export interface CurrentUser {
  /** True while connectCurrentUser is in flight. */
  loading: boolean;
  /**
   * The `connectCurrentUser` query error, or null. Set on a HARD error — the
   * query errored, was aborted by the request timeout (see client.ts), or
   * returned malformed data. This is a fail-CLOSED signal: `canSeeModule`
   * returns false for every gated module while it is non-null (a backend outage
   * must not unlock gated surfaces). Null while loading, on success, or when the
   * query is skipped (unauthenticated / dev override).
   */
  error: Error | null;
  /** Role code string from backend (e.g. "ADMIN"). Derived from connectCurrentUser.role.code. */
  role: string | null;
  /** Convenience: role === ROLES.ADMIN */
  isAdmin: boolean;
  /** The user's currently active persona (normalized to FE PersonaValue). */
  currentPersona: string | null;
  /** All personas assigned to this user (normalized to FE PersonaValues). */
  personas: string[];
  /**
   * Persona code -> the backend's display string ("PowerUser" -> "Power User").
   * A code absent from this map has no server-supplied label; render a local
   * fallback rather than the raw code. Empty in dev-override mode.
   */
  personaLabels: Partial<Record<string, string>>;
  /**
   * Group membership per persona code (the backend's RAW code -> group), for the
   * personas the backend assigned to a group (persona-groups epic). A persona
   * absent from this map is ungrouped — always optional-chain reads
   * (`personaGroups[p]?.displayName`). Empty in dev-override mode.
   */
  personaGroups: Partial<Record<string, { code: string; displayName: string }>>;
  /**
   * Group the CURRENT persona belongs to, mirroring `personaGroups` for
   * `currentPersona`. Null when ungrouped or dev-override is active. The group
   * fields are always requested now (the VITE_FEATURE_PERSONA_GROUPS gate was
   * removed 2026-08-05), so a null here means "ungrouped", not "not asked".
   */
  currentPersonaGroup: { code: string; displayName: string } | null;
  /**
   * Whether server-driven persona enforcement is ON in this environment
   * (`VITE_FEATURE_PERSONA_ENFORCEMENT`, ACT-1319). When true, a user with NO
   * current persona fails CLOSED on gated surfaces (unless the scoped admin
   * bypass applies); when false, the pre-enforcement allow-all seam is in effect.
   * Exposed so the sibling gates in useFeatureAccess (home cards / actions)
   * branch on the same value that `canSeeModule` uses.
   */
  enforcement: boolean;
  /**
   * Returns true if the current persona grants access to `moduleId`.
   * This is the PRIMARY member-facing module gate — call this, not
   * PERSONA_MODULES directly.
   *
   * SERVER-DRIVEN (ACT-1303): the decision is read straight off the backend
   * payload (`raw.currentPersona.modules[]`, which the backend has already
   * filtered to ACTIVE modules) — visible iff the module id is present.
   *
   * FAIL CLOSED ON ERROR: if the currentUser query has errored/aborted (`error`
   * non-null), this returns false for every gated module — a backend outage
   * hides gated surfaces rather than exposing them.
   *
   * NO-PERSONA SEAM SPLIT (ACT-1319): when there is no current persona and no
   * error, the decision depends on `enforcement`
   * (`VITE_FEATURE_PERSONA_ENFORCEMENT`): OFF → allow-all (the pre-enforcement
   * transition seam); ON → fail CLOSED (unprovisioned non-admins see only the
   * ungated tabs until an admin assigns them a persona).
   *
   * SCOPED ADMIN BYPASS (ACT-1319): the `admin` role gets all-access ONLY when
   * there is no REAL active persona (persona null OR the vestigial ADMIN
   * persona) — regardless of the flag. This fixes the empty-ADMIN-persona
   * lockout and keeps an admin with no persona fully enabled once enforcement
   * is on, WITHOUT breaking persona preview: an admin who selects a real
   * persona still sees exactly that persona's modules (consistent with
   * useVisibleHomeCards / the actions layer).
   */
  canSeeModule: (moduleId: string) => boolean;
  /**
   * Flattened set of permission value strings from connectCurrentUser.permissions.
   * Used as the SECONDARY gate within a persona's allowed surface.
   */
  permissionValues: Set<string>;
  /** Returns true if permissionValues contains `value`. Secondary gate. */
  hasPermission: (value: string) => boolean;
  /** Returns true if role matches the provided role string. */
  hasRole: (role: string) => boolean;
  /**
   * The raw connectCurrentUser payload, for consumers that need IPA/healthPlan/
   * lineOfBusiness data-scope checks from permissionMeta.
   * Null while loading or on error.
   */
  raw: CurrentUserResult["connectCurrentUser"];
  /**
   * DEV-ONLY: true when a `?persona=` / localStorage override is active and the
   * `connectCurrentUser` query has been short-circuited. Always false in
   * production. Lets the PersonaBadge switcher choose the offline (localStorage)
   * path over the mutation path. See CurrentUserProvider.
   */
  devOverrideActive: boolean;
  /**
   * DEV-ONLY: seed the active persona offline (no backend). Pass a PersonaValue
   * to activate that persona, or `null` for "no persona" (allow-all). No-op in
   * production. PersonaBadge calls this instead of the mutation when
   * `devOverrideActive` is true.
   */
  setDevPersona: (persona: PersonaValue | null) => void;
}

// ---------------------------------------------------------------------------
// Context + hook
// ---------------------------------------------------------------------------

const CurrentUserCtx = createContext<CurrentUser | null>(null);

/**
 * Access the current user's role, persona, and permissions.
 * Must be called inside <CurrentUserProvider>.
 */
export function useCurrentUser(): CurrentUser {
  const ctx = useContext(CurrentUserCtx);
  if (!ctx) throw new Error("useCurrentUser must be inside <CurrentUserProvider>");
  return ctx;
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

/**
 * Fetches `connectCurrentUser` once per session (after authentication) and
 * exposes role/persona/permission data to the entire app (ACT-1256 D12).
 *
 * `connectCurrentUser` is a care-management-gql proxy resolver that forwards
 * to the connect-gql AcCurrentUser type. Transport is the existing care-mgmt
 * Apollo client — no second client is required.
 *
 * Mount order: must be BELOW <Member360ApolloProvider> (needs the Apollo client)
 * and BELOW <AuthProvider> (needs isAuthenticated). In App.tsx this means:
 *   BrowserRouter → Member360ApolloProvider → CurrentUserProvider → ...
 *
 * Auth failures from this query route through the shared Apollo errorLink →
 * triggerExpiryWarning (same path as MemberProfilePage). No new logout path here.
 *
 * fetch policy: cache-first on initial load (stable mid-session), with
 * nextFetchPolicy: "cache-and-network" so revoked permissions don't persist
 * for the full cache lifetime (Rev 2 item 3).
 *
 * FAIL BEHAVIOR (ACT-1303 — corrected):
 * - loading: bounded by the CurrentUser query's request timeout (client.ts) — a
 *   hung backend aborts into `error` rather than spinning forever.
 * - HARD ERROR (query errored / aborted / malformed payload): `canSeeModule`
 *   fails CLOSED — returns false for every gated module id, so a backend outage
 *   HIDES gated tabs/cards instead of exposing them. Mirrors App.tsx's
 *   ProtectedRoute (`error && hasRbacRequirement` → block). Uncontrolled surfaces
 *   (tabs with no controlling module id, e.g. Diagnoses/Labs) are unaffected —
 *   they never consult canSeeModule — so the base clinical view survives.
 * - NO PERSONA PAYLOAD, no error (raw.currentPersona == null): the LEGITIMATE
 *   pre-enforcement transition seam → allow-all, so real users keep the full
 *   pre-RBAC surface until every env returns persona data. This is NOT the same
 *   as the error case: seam = allow-all, error = fail-closed.
 * - persona payload present: visible IFF the module id is in
 *   raw.currentPersona.modules[] (omitted module → fail-closed).
 */
export function CurrentUserProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, accountLabel } = useAuthContext();

  // DEV-ONLY offline override. Tri-state, read once on mount:
  //   - PersonaValue → seed that persona label, short-circuit the query;
  //   - null         → explicit "no persona" (allow-all), short-circuit too;
  //   - undefined    → no dev intent → run the normal backend query.
  // NOTE: since module visibility is now SERVER-DRIVEN (canSeeModule /
  // useVisibleModules / useVisibleHomeCards read raw.currentPersona.modules,
  // which this override does NOT synthesize), a dev override sets only the
  // persona *label* — it hits the allow-all seam and does NOT gate tabs/cards.
  // Per-persona visibility must be verified against a REAL backend persona
  // payload, not this override. Guarded on import.meta.env.DEV
  // (readDevPersonaOverride returns undefined in prod), so prod is unchanged.
  const [devPersona, setDevPersonaState] = useState<
    PersonaValue | null | undefined
  >(() => readDevPersonaOverride());
  const hasDevIntent = devPersona !== undefined;

  // Persist + adopt a dev-path selection. null = explicit "no persona" clear.
  const setDevPersona = useCallback((persona: PersonaValue | null) => {
    if (!import.meta.env.DEV) return;
    try {
      if (persona) window.localStorage.setItem(DEV_PERSONA_STORAGE_KEY, persona);
      else window.localStorage.removeItem(DEV_PERSONA_STORAGE_KEY);
    } catch {
      /* ignore storage failures */
    }
    setDevPersonaState(persona);
  }, []);

  // Only request the persona's clinical-summary template title where the backend
  // (care-management-gql, ACT-1295) has shipped the field — otherwise the field
  // would fail the whole CurrentUser query at validation time (see currentUser.ts).
  const includeClinicalSummaryTemplate =
    import.meta.env.VITE_FEATURE_CLINICAL_SUMMARY_TEMPLATE_TITLE === "true";
  // The persona-groups fields (groupCode/groupDisplayName, ACT-1233/ACT-1256) are
  // always requested — the VITE_FEATURE_PERSONA_GROUPS gate was removed 2026-08-05
  // because persona groups ships in AstraConnect's first release rather than as a
  // phased rollout. The deploy-order requirement it used to enforce still applies and
  // is documented on PERSONA_GROUP_SELECTION in currentUser.ts.
  const currentUserQueryDoc = useMemo(
    () => buildCurrentUserQuery(includeClinicalSummaryTemplate),
    [includeClinicalSummaryTemplate],
  );

  const { data, loading, error } = useQuery<CurrentUserResult>(
    currentUserQueryDoc,
    {
      // Suppress the doomed query whenever a dev override is in effect.
      skip: !isAuthenticated || hasDevIntent,
      fetchPolicy: "cache-first",
      nextFetchPolicy: "cache-and-network",
      // errorPolicy: "all" so partial data is still usable if one field errors
      errorPolicy: "all",
    },
  );

  const user = data?.connectCurrentUser ?? null;

  // Normalize backend persona code strings to canonical FE PersonaValues.
  //
  // SECURITY: there are three distinct states:
  //   - null  → backend returned NO persona field at all (the QA/dev seam where
  //             connectCurrentUser doesn't yet return persona data). This is the
  //             intentional ALLOW-ALL transition path — failing closed here would
  //             hide every gated surface for real users until the backend is fully
  //             deployed. DELETE this allow-all branch only once persona data is
  //             returned on all envs.
  //   - known PersonaValue → recognized backend code; gated on the backend's grant
  //             set (ALLOW only modules in `currentPersona.modules[]`).
  //   - non-null but unrecognized code → the raw string is KEPT. We do NOT fall
  //             back to null, because null is the allow-all seam and a new/unknown
  //             code must not unlock everything.
  //
  // ⚠️ CORRECTION (2026-08-26). This comment used to claim an unrecognized code
  // "won't match any PERSONA_MODULES key, so canSeeModule returns false for every
  // module (DENY)". That is FALSE — it described the compile-time lookup ACT-1303
  // deleted, and it misled a reader into believing new personas fail closed.
  // `canSeeModule` branch (4) reads `user.currentPersona.modules[]` straight off
  // the backend payload and never consults `normalizePersona`,
  // `BACKEND_PERSONA_MAP`, or `PERSONA_MODULES` (which roles.ts documents as a
  // NON-RUNTIME parity fixture). So a persona code this map does not know still
  // gets EXACTLY the modules the backend granted it: module visibility AND tab
  // order are fully server-driven, and a new persona needs NO frontend change.
  // Verified against live QA, where `Clinical` and `PowerUser` — created through
  // the admin console and absent from this map — carry 16 active grants each.
  //
  // What an undefined `normalizePersona` result DOES still affect: the display
  // string, the `personas` list, picker filtering via `isHiddenPersona`, and
  // `noRealPersona` — which keys ONLY on `isHiddenPersona` (i.e. "ADMIN"), so an
  // unrecognized code counts as a REAL persona and correctly does not trigger the
  // scoped admin bypass.
  //
  // Do NOT "fix" this by adding persona codes to BACKEND_PERSONA_MAP to make
  // visibility work — that would reintroduce a hard-coded client-side allowlist
  // overriding a server-driven decision, the same defect class as the
  // UNGATED_MODULES set removed in AC-ADM-3.
  //
  // The actions layer (resolveModuleActions in useFeatureAccess.ts) independently
  // uses raw.currentPersona == null for its allow-all seam. Both layers agree on
  // "no backend persona data" → allow-all.
  //
  // DEV override wins: seed the persona offline (null = explicit allow-all).
  const currentPersona = useMemo(
    () => {
      if (hasDevIntent) return devPersona ?? null;
      const raw = user?.currentPersona?.code;
      if (!raw) return null; // no persona data from backend → allow-all seam
      return normalizePersona(raw) ?? raw; // unrecognized → keep raw (DENY via canSeeModule)
    },
    [hasDevIntent, devPersona, user?.currentPersona?.code],
  );

  // In dev-override mode expose the built-in personas so the switcher can cycle
  // them offline; otherwise use the backend's list VERBATIM.
  //
  // This used to map every code through `normalizePersona` and drop whatever came
  // back undefined — a SECOND hard-coded client-side allowlist, on the selection
  // path rather than the visibility path. Live QA proved the cost (2026-08-27):
  // `connectCurrentUser` returned six personas including `Clinical` and
  // `PowerUser` (both created through the admin console), an admin could assign
  // them to a user, and they still never reached the switcher — so nobody could
  // select them. A persona the server grants must be selectable without a
  // frontend release; that is the same principle as removing UNGATED_MODULES, and
  // this path was missed when that one was fixed.
  //
  // Only `isHiddenPersona` filters now, and that is a deliberate product rule
  // (ADMIN is vestigial — admin access comes from the ROLE, ACT-1303), not a
  // vocabulary gate.
  const personas = useMemo(
    () =>
      hasDevIntent
        ? ALL_PERSONAS
        : (user?.personas ?? [])
            .map((p) => p.code)
            .filter((code) => !isHiddenPersona(code)),
    [hasDevIntent, user?.personas],
  );

  // code -> the backend's own display string, so the switcher renders "Power
  // User" instead of a locally-derived guess from the code ("Poweruser").
  const personaLabels = useMemo(() => {
    if (hasDevIntent) return {};
    const map: Partial<Record<string, string>> = {};
    for (const p of user?.personas ?? []) {
      if (p.displayName) map[p.code] = p.displayName;
    }
    return map;
  }, [hasDevIntent, user?.personas]);

  // Group membership per persona (persona-groups epic). Empty in dev-override
  // mode (the offline override seeds only a persona label, not group data) and
  // whenever the backend/env hasn't shipped groupCode/groupDisplayName — a
  // persona simply missing from the map reads the same as "ungrouped".
  const personaGroups = useMemo(() => {
    if (hasDevIntent) return {};
    // Keyed by the backend's RAW code, for the same reason `personas` is: keying
    // by the normalized value silently dropped the group of any persona this
    // frontend does not know, so a grouped `Clinical` would have lost its group
    // even once it became selectable.
    const map: Partial<Record<string, { code: string; displayName: string }>> = {};
    for (const p of user?.personas ?? []) {
      if (!isHiddenPersona(p.code) && p.groupCode && p.groupDisplayName) {
        map[p.code] = { code: p.groupCode, displayName: p.groupDisplayName };
      }
    }
    return map;
  }, [hasDevIntent, user?.personas]);

  // Mirrors personaGroups for the CURRENT persona specifically.
  const currentPersonaGroup = useMemo(() => {
    if (hasDevIntent) return null;
    const cp = user?.currentPersona;
    return cp?.groupCode && cp?.groupDisplayName
      ? { code: cp.groupCode, displayName: cp.groupDisplayName }
      : null;
  }, [hasDevIntent, user?.currentPersona]);

  const permissionValues = useMemo(
    () =>
      new Set(
        (user?.permissions ?? []).map((p) => p.value),
      ),
    [user?.permissions],
  );

  // role is the code string from the AcRole object (e.g. "ADMIN").
  const role = user?.role?.code ?? null;
  const isAdmin = role === ROLES.ADMIN;

  // Server-driven persona enforcement toggle (ACT-1319). Read from the module-
  // level featureFlags singleton — the value is fixed for the session, so
  // reading it here (rather than in the closure) keeps the memo deps honest.
  const enforcement = featureFlags.personaEnforcement;

  // "No REAL active persona" — either the backend returned no current persona at
  // all, or it returned the vestigial ADMIN persona (hidden code, no grants).
  // The scoped admin bypass (below) keys on this so an admin previewing a REAL
  // persona is NOT granted all-access.
  const noRealPersona =
    user?.currentPersona == null || isHiddenPersona(user.currentPersona.code);

  // clinic carries the IPA code (e.g. "H1234") — an opaque identifier, not a
  // display name. Non-PHI per ACT-1037; safe to emit as a custom dimension.
  const clinic =
    user?.permissions?.find((p) => p.permissionMeta?.ipas?.length)
      ?.permissionMeta?.ipas?.[0] ?? "(none)";

  // Sync global App Insights custom dimensions whenever persona / IPA / role /
  // email changes. Dims are read at emit-time in the telemetry initializer so
  // this keeps dashboard breakdowns accurate across persona switches mid-session.
  // email is the staff account email (MSAL username or B2C claim) — not member
  // PHI (ACT-1037). It lets usage queries filter/group by individual user.
  useEffect(() => {
    setGlobalDimensions({
      persona: currentPersona ?? "(none)",
      clinic,
      role: role ?? "(none)",
      email: accountLabel ?? "(none)",
    });
  }, [currentPersona, clinic, role, accountLabel]);

  // HARD ERROR signal (ACT-1303): the currentUser query errored / aborted (or
  // returned an unusable payload) — distinct from the no-persona-payload seam.
  // A dev override skips the query entirely, so it is never a hard error.
  // Drives canSeeModule (and useVisibleHomeCards, via the exposed `error`) to
  // fail CLOSED rather than allow-all. See the FAIL BEHAVIOR docstring above.
  const hardError = !hasDevIntent && error != null;

  const value = useMemo<CurrentUser>(
    () => ({
      // A dev override resolves synchronously — never report it as loading.
      loading: hasDevIntent ? false : loading,
      error: error ?? null,
      role,
      isAdmin,
      currentPersona,
      personas,
      personaLabels,
      personaGroups,
      currentPersonaGroup,
      enforcement,
      canSeeModule: (moduleId: string) => {
        // (1) HARD ERROR → fail CLOSED. Checked FIRST so a backend outage cannot
        // fall through to any allow branch below (the bug this fixes: an errored
        // query left user?.currentPersona == null, which the seam read as
        // allow-all → every gated tab/card visible on outage). Mirrors App.tsx
        // ProtectedRoute's `error && hasRbacRequirement → block`.
        if (hardError) return false;
        // (2) SCOPED ADMIN BYPASS (ACT-1319) — the `admin` role gets all-access
        // ONLY when there is no REAL active persona (persona null OR the
        // vestigial ADMIN persona). This is a deliberate, narrow divergence from
        // ACT-1303's strict "no admin bypass on visibility": it fixes the
        // empty-ADMIN-persona lockout and keeps an unprovisioned admin fully
        // enabled once enforcement is on, WITHOUT breaking preview — an admin who
        // selects a REAL persona has noRealPersona === false and falls through to
        // the persona-driven branch (3), seeing exactly that persona's modules.
        if (isAdmin && noRealPersona) return true;
        // (3) NO CURRENT PERSONA, no error — the seam split (ACT-1319):
        //   - enforcement OFF → allow-all (the pre-enforcement transition seam);
        //   - enforcement ON  → fail CLOSED (an unprovisioned non-admin sees only
        //     the ungated tabs until an admin assigns a persona).
        // (Admins with no real persona are already handled by (2) above.)
        if (user?.currentPersona == null) return !enforcement;
        // (4) Server-driven, fail-closed: visible iff present in the persona's
        // (already active-filtered) modules. Intentionally NO admin-role
        // all-access here — member-facing visibility follows the active persona
        // (an admin previewing a real persona lands here), consistent with
        // useVisibleHomeCards.
        return user.currentPersona.modules.some((m) => m.moduleId === moduleId);
      },
      permissionValues,
      hasPermission: (v: string) => permissionValues.has(v),
      hasRole: (r: string) => role === r,
      raw: user,
      devOverrideActive: hasDevIntent,
      setDevPersona,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      hasDevIntent,
      loading,
      error,
      hardError,
      role,
      isAdmin,
      enforcement,
      noRealPersona,
      currentPersona,
      personas,
      personaLabels,
      personaGroups,
      currentPersonaGroup,
      permissionValues,
      user,
      setDevPersona,
    ],
  );

  return (
    <CurrentUserCtx.Provider value={value}>{children}</CurrentUserCtx.Provider>
  );
}
